import { useCallback, useEffect, useState } from "react"
import {
  MblsAddress,
  convertToMblsAddress,
} from "./convertArcgisCandidateToMblsAddress"

interface AddressCandidate {
  address: string
  location: {
    x: number
    y: number
  }
  score: number
  attributes: Record<string, any>
}

interface ArcgisFindAddressCandidatesHook {
  candidates: AddressCandidate[]
  mblsAddresses: MblsAddress[]
  isLoading: boolean
  error: string | null
  findCandidates: (singleLine: string, magicKey: string) => void
}

const useArcgisFindAddressCandidates = (): ArcgisFindAddressCandidatesHook => {
  const [candidates, setCandidates] = useState<AddressCandidate[]>([])

  const [mblsAddresses, setMblsAddresses] = useState<MblsAddress[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [error, setError] = useState<string | null>(null)

  const handleMessage = useCallback((event: MessageEvent) => {
    // assert
    if (event?.data === undefined) {
      console.error("useArcgisFindAddressCandidates: event.data is undefined")
      return
    }
    const { type, candidates, error } = event.data

    if (type === "arcgisAddressCandidates") {
      // The host answers with whatever ArcGIS returned. An error response
      // carries no `candidates` array at all, and mapping over `undefined`
      // threw here before LOCKER-410 — inside a `message` listener, where
      // React cannot turn it into an error boundary.
      const list: any[] = Array.isArray(candidates) ? candidates : []

      setCandidates(list)

      setMblsAddresses(list.map((c: any) => convertToMblsAddress(c)))

      setIsLoading(false)

      setError(error)
    }
  }, [])

  useEffect(() => {
    window.addEventListener("message", handleMessage)
    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [handleMessage])

  /**
   * Ask the host to resolve ONE ArcGIS suggestion into a full address.
   *
   * `magicKey` is mandatory (LOCKER-410 `DoD3`). Before that, the dialog called
   * this on every keystroke with `magicKey: ""`, which fired an unpinned
   * `findAddressCandidates` per character — n-1 wasted geocodes, each able to
   * return a candidate from anywhere on earth because only `getSuggestions`
   * pins `countryCode` host-side. A request without a magic key is not a
   * cheaper lookup, it is a different and worse one, so it is refused here
   * rather than merely avoided at the call site.
   */
  const findCandidates = useCallback((singleLine: string, magicKey: string) => {
    setIsLoading(true)

    setError(null)

    if (singleLine.length < 3 || !magicKey) {
      setIsLoading(false)

      setCandidates([])

      setMblsAddresses([])
      return
    }
    window.parent.postMessage(
      {
        type: "getArcgisAddressCandidates",
        payload: {
          singleLine,
          magicKey,
          maxLocations: 1,
          outFields: "*",
          category: "Address,Postal",
          forStorage: true,
        },
      },
      "*",
    )
  }, [])

  return {
    candidates,
    mblsAddresses,
    isLoading,
    error,
    findCandidates,
  }
}

export default useArcgisFindAddressCandidates
